import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  BackgroundMusicDto,
  BackgroundMusicOwnershipFilterDto,
  PagedBackgroundMusicsResponseDto,
} from "@repo/api/background-music";
import { BackgroundMusicApi } from "../api/background-music-api";
import Toast from "@/lib/components/Toast";

const BACKGROUND_MUSIC_PAGE_SIZE = 25;

export type BackgroundMusicFilters = {
  search: string;
  ownership: BackgroundMusicOwnershipFilterDto;
  genres: string[];
  moods: string[];
  tags: string[];
  favoritesOnly: boolean;
};

type BackgroundMusicCursor =
  | { isTeamTrack: boolean; name: string; externalId: string }
  | undefined;

export const backgroundMusicQueryKeys = {
  root: () => ["background-music"],
  paged: (teamExternalId: string, filters: BackgroundMusicFilters) => [
    "background-music",
    "paged",
    teamExternalId,
    filters,
  ],
  metadata: (teamExternalId: string) => [
    "background-music",
    "metadata",
    teamExternalId,
  ],
  byExternalIds: (teamExternalId: string, externalIds: string[]) => [
    "background-music",
    "by-ids",
    { teamExternalId, externalIds },
  ],
};

export function usePagedBackgroundMusicsQuery(
  teamExternalId: string,
  filters: BackgroundMusicFilters,
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: backgroundMusicQueryKeys.paged(teamExternalId, filters),
    queryFn: ({ pageParam }) =>
      BackgroundMusicApi.getPagedBackgroundMusics(teamExternalId, {
        pageSize: BACKGROUND_MUSIC_PAGE_SIZE,
        search: filters.search || undefined,
        ownership: filters.ownership,
        genres: filters.genres.length > 0 ? filters.genres : undefined,
        moods: filters.moods.length > 0 ? filters.moods : undefined,
        tags: filters.tags.length > 0 ? filters.tags : undefined,
        favoritesOnly: filters.favoritesOnly || undefined,
        cursorIsTeamTrack: pageParam?.isTeamTrack,
        cursorName: pageParam?.name,
        cursorExternalId: pageParam?.externalId,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as BackgroundMusicCursor,
    enabled: (options?.enabled ?? true) && !!teamExternalId,
    staleTime: Infinity,
    meta: {
      errorMessage: "Failed to fetch background music",
    },
  });
}

export function useBackgroundMusicMetadataQuery(
  teamExternalId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: backgroundMusicQueryKeys.metadata(teamExternalId),
    queryFn: () =>
      BackgroundMusicApi.getBackgroundMusicMetadata(teamExternalId),
    enabled: (options?.enabled ?? true) && !!teamExternalId,
    staleTime: Infinity,
    meta: {
      errorMessage: "Failed to fetch background music",
    },
  });
}

export function useBackgroundMusicsByExtIdsQuery(
  teamExternalId: string,
  externalIds: string[],
  options?: { enabled?: boolean },
) {
  const sortedIds = [...externalIds].sort();

  return useQuery({
    queryKey: backgroundMusicQueryKeys.byExternalIds(teamExternalId, sortedIds),
    queryFn: () =>
      BackgroundMusicApi.getBackgroundMusicsByExtIds(teamExternalId, sortedIds),
    enabled:
      (options?.enabled ?? true) && !!teamExternalId && sortedIds.length > 0,
    staleTime: Infinity,
    meta: {
      errorMessage: "Failed to fetch background music",
    },
  });
}

export function useInvalidateBackgroundMusicsQuery() {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: backgroundMusicQueryKeys.root(),
    });
}

// Flips isFavorite for a track across every cached background-music query,
// covering both the infinite paged shape and the by-ids array shape.
function setFavoriteInCaches(
  data: unknown,
  externalId: string,
  isFavorite: boolean | undefined,
): unknown {
  if (!data) return data;

  const apply = (music: BackgroundMusicDto): BackgroundMusicDto =>
    music.externalId === externalId
      ? { ...music, isFavorite: isFavorite ?? !music.isFavorite }
      : music;

  if (typeof data === "object" && "pages" in data) {
    const infinite = data as {
      pages: PagedBackgroundMusicsResponseDto[];
      pageParams: unknown[];
    };
    return {
      ...infinite,
      pages: infinite.pages.map((page) => ({
        ...page,
        backgroundMusics: page.backgroundMusics.map(apply),
      })),
    };
  }

  if (Array.isArray(data)) {
    return (data satisfies BackgroundMusicDto[]).map(apply);
  }

  return data;
}

// Drops a track from every cached background-music query, covering both the
// infinite paged shape and the by-ids array shape. Used to remove a deleted
// track without invalidating (which would reset the paged list to the start).
export function removeBackgroundMusicFromCaches(
  data: unknown,
  externalId: string,
): unknown {
  if (!data) return data;

  if (typeof data === "object" && "pages" in data) {
    const infinite = data as {
      pages: PagedBackgroundMusicsResponseDto[];
      pageParams: unknown[];
    };
    return {
      ...infinite,
      pages: infinite.pages.map((page) => ({
        ...page,
        backgroundMusics: page.backgroundMusics.filter(
          (music) => music.externalId !== externalId,
        ),
      })),
    };
  }

  if (Array.isArray(data)) {
    return (data satisfies BackgroundMusicDto[]).filter(
      (music) => music.externalId !== externalId,
    );
  }

  return data;
}

export function useToggleBackgroundMusicFavoriteMutation(
  teamExternalId: string,
) {
  const queryClient = useQueryClient();
  const rootKey = backgroundMusicQueryKeys.root();

  return useMutation({
    mutationFn: (externalId: string) =>
      BackgroundMusicApi.toggleBackgroundMusicFavorite(
        teamExternalId,
        externalId,
      ),
    onMutate: async (externalId: string) => {
      await queryClient.cancelQueries({ queryKey: rootKey });

      const previous = queryClient.getQueriesData({ queryKey: rootKey });

      queryClient.setQueriesData({ queryKey: rootKey }, (data) =>
        setFavoriteInCaches(data, externalId, undefined),
      );

      return { previous };
    },
    onError: (_error, _externalId, context) => {
      context?.previous.forEach(([key, data]) =>
        queryClient.setQueryData(key, data),
      );
      Toast.error("Failed to update favourite");
    },
    onSuccess: ({ isFavorite }, externalId) => {
      queryClient.setQueriesData({ queryKey: rootKey }, (data) =>
        setFavoriteInCaches(data, externalId, isFavorite),
      );
    },
    onSettled: () => {
      // Reconcile favourites-tab membership (a removed favourite drops out).
      queryClient.invalidateQueries({
        queryKey: [...backgroundMusicQueryKeys.root(), "paged"],
      });
    },
  });
}
